import { contentBaseUrl } from "site/services/content-base-url";
import { getBookContentKeyBytesFromSession } from "site/services/auth-session.service";
import { toBufferSource } from "site/utils";

type EncryptedBlob = {
  iv: string;
  cipher: string;
};

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const BOOKS_CATALOG_URL = `${contentBaseUrl()}/books/books.json`;

let textIdToBookIdPromise: Promise<Map<string, string>> | null = null;

type BooksCatalogEntry = {
  id: number;
  texts?: unknown[];
};

function b64ToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function isEncryptedBlob(value: unknown): value is EncryptedBlob {
  const v = value as EncryptedBlob;
  return !!v && typeof v.iv === "string" && typeof v.cipher === "string";
}

async function getTextIdToBookIdMap(): Promise<Map<string, string>> {
  if (!textIdToBookIdPromise) {
    textIdToBookIdPromise = fetch(BOOKS_CATALOG_URL)
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error(`Failed to load books catalog: ${resp.status}`);
        }
        const books = (await resp.json()) as BooksCatalogEntry[];
        const map = new Map<string, string>();
        for (const book of books) {
          const bid = String(book.id);
          const texts = book.texts;
          if (!Array.isArray(texts)) continue;
          for (const tid of texts) {
            const idStr = String(tid);
            if (!map.has(idStr)) {
              map.set(idStr, bid);
            }
          }
        }
        return map;
      })
      .catch((error) => {
        textIdToBookIdPromise = null;
        throw error;
      });
  }
  return textIdToBookIdPromise;
}

async function resolveBookIdForTextAsset(assetId: string): Promise<string | null> {
  const map = await getTextIdToBookIdMap();
  return map.get(assetId) ?? null;
}

async function decryptContent(encrypted: EncryptedBlob, contentKeyBytes: Uint8Array, aadLabel: string): Promise<string> {
  const key = await crypto.subtle.importKey("raw", toBufferSource(contentKeyBytes), { name: "AES-GCM" }, false, ["decrypt"]);
  const iv = b64ToBytes(encrypted.iv);
  const cipher = b64ToBytes(encrypted.cipher);
  const aad = encoder.encode(aadLabel);
  const plainBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toBufferSource(iv), additionalData: aad },
    key,
    toBufferSource(cipher),
  );
  return decoder.decode(new Uint8Array(plainBuffer));
}

function resolveBookContentKeyBytes(bookId: string): Uint8Array {
  const fromSession = getBookContentKeyBytesFromSession(bookId);
  if (fromSession) {
    return fromSession;
  }
  throw new Error(`book content key missing in session for book ${bookId}`);
}

export type DecryptExerciseAssetOptions = {
  isFree?: boolean;
};

export async function decryptExerciseAsset(
  asset: unknown,
  exerciseAssetId: string,
  opts?: DecryptExerciseAssetOptions,
): Promise<any> {
  if (opts?.isFree) {
    return asset;
  }

  if (!Array.isArray(asset) || !asset[0] || !Array.isArray((asset[0] as { zepto?: unknown }).zepto)) {
    return asset;
  }

  const assetId = `${exerciseAssetId}`;
  if (!assetId) {
    throw new Error("decryptExerciseAsset: empty exercise id");
  }

  const root = asset[0] as { zepto: unknown[] };
  const needsBookKey = root.zepto.some(
    (row) => Array.isArray(row) && row.length >= 3 && isEncryptedBlob(row[1]),
  );
  if (!needsBookKey) {
    return asset;
  }

  const bookId = await resolveBookIdForTextAsset(assetId);
  if (bookId == null || bookId === "") {
    throw new Error(`Exercise ${assetId} is not found in any book in books.json`);
  }

  const contentKeyBytes = resolveBookContentKeyBytes(bookId);

  const nextZepto = await Promise.all(
    root.zepto.map(async (row) => {
      if (!Array.isArray(row) || row.length < 3) return row;
      const rowType = row[0];
      const rowPayload = row[1];
      const rowId = row[2];
      if (!isEncryptedBlob(rowPayload)) {
        return row;
      }
      const aad = `${bookId}|${assetId}|${String(rowType)}|${String(rowId)}`;
      const plainContent = await decryptContent(rowPayload, contentKeyBytes, aad);
      return [rowType, JSON.parse(plainContent), rowId];
    }),
  );

  return [{ ...(asset[0] as Record<string, unknown>), zepto: nextZepto }];
}
